// frontend/src/components/Banner.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cmsService } from '../services/cmsService';
import config from '../config';

export const Banner = ({ position = 'home_hero' }) => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBanners();
  }, [position]);

  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await cmsService.getBanners(position);
      
      let bannersData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          bannersData = response.data;
        } else if (response.data.results) {
          bannersData = response.data.results;
        } else {
          bannersData = Object.values(response.data);
        }
      }
      
      setBanners(bannersData);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error fetching banners:', error);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    if (banners.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  };

  const prevSlide = () => {
    if (banners.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  if (loading) {
    return (
      <div className="w-full h-96 bg-gray-200 animate-pulse"></div>
    );
  }

  if (!banners || banners.length === 0) {
    return null;
  }

  const currentBanner = banners[currentIndex];
  
  if (!currentBanner) {
    return null;
  }

  return (
    <div className="relative w-full overflow-hidden shadow-xl"> {/* حذف rounded-3xl */}
      {/* تصویر بنر */}
      <div className="relative">
        <img
          src={currentBanner.image ? config.getMediaUrl(currentBanner.image) : '/placeholder-banner.jpg'}
          alt={currentBanner.title || 'بنر'}
          className="w-full h-96 md:h-[500px] object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = '/placeholder-banner.jpg';
          }}
        />
        
        {/* لایه تیره روی تصویر */}
        <div className="absolute inset-0 bg-black/50"></div>
        
        {/* محتوای متن و دکمه‌ها */}
        <div className="absolute bottom-0 left-0 right-0 px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 drop-shadow-lg">
              {currentBanner.title || ''}
            </h2>
            {currentBanner.subtitle && (
              <p className="text-lg md:text-xl lg:text-2xl text-white/90 mb-6 md:mb-8 drop-shadow">
                {currentBanner.subtitle}
              </p>
            )}
            
            {/* دکمه‌ها */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pb-4">
              {/* دکمه اول - زرد */}
              <Link
                to={currentBanner.link_url || '/products'}
                className="inline-block bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-3 md:px-10 md:py-4 rounded-2xl font-bold text-base md:text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {currentBanner.button_text || 'مشاهده محصولات'}
              </Link>
              
              {/* دکمه دوم - سبز روشن */}
              <Link
                to="/installation/request"
                className="inline-block bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 md:px-10 md:py-4 rounded-2xl font-bold text-base md:text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                درخواست نصب
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* دکمه‌های قبلی و بعدی */}
      {banners.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 md:p-3 rounded-full transition shadow-md z-10"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-800" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 md:p-3 rounded-full transition shadow-md z-10"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-800" />
          </button>
        </>
      )}

      {/* نقاط نشانگر */}
      {banners.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-white w-6' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
